import { debounce } from './debounce';
import mutationCavernsMobDeath from '../graphql/caverns/mutationCavernsMobDeath';
import { ApiErrorMessage } from 'dt-common/types/ApiErrorMessages';
import { GraphQlRequestError } from '~/graphql/graphqlClient';

type MobDeathEvent = {
  mob_id: string;
  deathTileId: string;
  livingHeroHandles: string[];
};

export function createMobDeathBatcher({
  getPlayerId,
  getGameSubmode,
  onLootEvents,
  onDeathRegistered,
  debounceMs = 700,
}: {
  getPlayerId: () => string;
  getGameSubmode: () => string;
  onLootEvents: (events: Array<{ deathTileId: string; death_tile_loot: any; }>) => void;
  onDeathRegistered: (num_remaining_mobs: number) => void;
  debounceMs?: number;
}) {
  const pendingByMobId = new Map<string, MobDeathEvent>();
  let flushInFlight = false;

  const flush = async () => {
    if (flushInFlight) return;
    if (pendingByMobId.size === 0) return;

    flushInFlight = true;
    const mob_deaths = Array.from(pendingByMobId.values());
    pendingByMobId.clear();

    try {
      const res = await mutationCavernsMobDeath({
        playerId: getPlayerId(),
        game_submode: getGameSubmode(),
        mob_deaths,
      });

      if (res?.death_tile_loot_events?.length) {
        onLootEvents(res.death_tile_loot_events as any);
      }

      if (res.success && !isNaN(res.num_remaining_mobs)) {
        onDeathRegistered(res.num_remaining_mobs);
      }
    } catch (err) {
      if (err instanceof GraphQlRequestError && err.errors.find((error) => error.message === ApiErrorMessage.NO_CURRENT_RUN_FOUND)) {
        postMessage({ message_name: 'missing_caverns_run_data' });
        return;
      }
      // Re-queue so a later event can retry.
      for (const e of mob_deaths) pendingByMobId.set(e.mob_id, e);
      console.error(err);
    } finally {
      flushInFlight = false;
      if (pendingByMobId.size > 0) debouncedFlush();
    }
  };

  const debouncedFlush = debounce(() => {
    void flush();
  }, debounceMs);

  return {
    queue: (event: MobDeathEvent) => {
      pendingByMobId.set(event.mob_id, event);
      debouncedFlush();
    },
    flushNow: () => flush(),
  };
}


